import React, { useState } from 'react';
import { Label, Input } from '@rebass/forms';
import { Box, Button, Flex } from 'rebass';
import { FiArrowRight, FiPlus, FiTrash2 } from 'react-icons/fi';
import { cloneDeep, isEqual } from 'lodash';
import styled from 'styled-components';

import InputError from 'components/InputError';
import { Combobox } from 'components/Combobox';
import { PERSON_KEY_TYPE_TEXT, SCREENER_INTEGRATION_DEFAULT_FIELDS } from 'utils/constants';
import helpersScreener from 'utils/helpers-screener';

const CustomFieldsGrid = styled.div`
    display: grid;
    grid-template-columns: 10fr 1fr 10fr 1fr;
    gap: 8px;
    align-items: center;
    margin-bottom: 8px;
`;

// Max safe url length is 2000 chars
// Base survey url length with default fields = 256 chars
// Each custom field max 333 chars
const MAX_CUSTOM_FIELDS = 5;

// Regex pattern to validate property_key: only allows letters, numbers, underscores, and dashes
const propertyKeyValidPattern = /^[a-zA-Z0-9_-]*$/;

function ScreenerIntegrationCustomFields({ panelProperties, value = [], onChange, showSaveButton = false, isSaving }) {
    const [customFields, setCustomFields] = useState(() => cloneDeep(value));
    const isSaveButtonDisabled =
        isSaving || isEqual(customFields, value) || !helpersScreener.areCustomFieldsValid(customFields);

    const handleAddField = () => {
        const newField = { property_id: '', property_key: '' };
        const updatedFields = [...customFields, newField];
        setCustomFields(updatedFields);
        if (onChange && !showSaveButton) {
            onChange(updatedFields);
        }
    };

    const handleDeleteField = index => {
        const updatedFields = customFields.toSpliced(index, 1);
        setCustomFields(updatedFields);
        if (onChange && !showSaveButton) {
            onChange(updatedFields);
        }
    };

    const handleFieldChange = (index, field, value) => {
        // Validate property_key: only allow letters, numbers, underscores, and dashes
        if (field === 'property_key') {
            if (!propertyKeyValidPattern.test(value)) {
                return; // Reject invalid input
            }
        }

        const updatedFields = [...customFields];
        updatedFields[index][field] = value;
        setCustomFields(updatedFields);
        if (onChange && !showSaveButton) {
            onChange(updatedFields);
        }
    };

    const handleSave = () => {
        if (onChange) {
            onChange(customFields);
        }
    };

    // Check if a property_id is already used by another field
    const isPropertyIdUsed = (propertyId, currentIndex) => {
        return customFields.some((field, idx) => idx !== currentIndex && field.property_id == propertyId);
    };

    // Check if a property_key is already used by another field or default fields (case-insensitive)
    const isPropertyKeyUsed = (propertyKey, currentIndex) => {
        if (!propertyKey) return false;
        const lowerKey = propertyKey.toLowerCase();

        // Check against default fields
        const isDefaultKey = SCREENER_INTEGRATION_DEFAULT_FIELDS.some(field => field.key.toLowerCase() === lowerKey);
        if (isDefaultKey) return true;

        // Check against other custom fields
        return customFields.some(
            (field, idx) => idx !== currentIndex && (field.property_key || '').toLowerCase() === lowerKey
        );
    };

    // Get available properties for a specific field index
    const getAvailableProperties = currentIndex => {
        if (!panelProperties) return [];
        return panelProperties
            .filter(property => property.type !== PERSON_KEY_TYPE_TEXT && !isPropertyIdUsed(property.id, currentIndex))
            .map(property => ({ value: property.id, label: property.title }));
    };

    return (
        <Box>
            <Box className="gray fs-13" mb={3}>
                Use Custom Fields to send panelist data to your external survey{' '}
                <a href="#" target="_blank" rel="noopener noreferrer" className="help-question">
                    ?
                </a>
            </Box>
            <CustomFieldsGrid>
                <Box className="fs-accent-12 text-secondary-dark">Panel Property</Box>
                <Box></Box>
                <Box className="fs-accent-12 text-secondary-dark">Embedded Data</Box>
                <Box></Box>

                {SCREENER_INTEGRATION_DEFAULT_FIELDS.map(field => (
                    <React.Fragment key={field.key}>
                        <Input type="text" readOnly disabled value={field.label} />
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <FiArrowRight />
                        </Box>
                        <Input type="text" readOnly disabled value={field.key} />
                        <Box></Box>
                    </React.Fragment>
                ))}
                {customFields.map((field, index) => {
                    const isDuplicateKey = field.property_key && isPropertyKeyUsed(field.property_key, index);
                    return (
                        <React.Fragment key={index}>
                            <Combobox
                                items={getAvailableProperties(index)}
                                value={field.property_id || null}
                                onChange={value => handleFieldChange(index, 'property_id', value)}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <FiArrowRight />
                            </Box>
                            <Input
                                type="text"
                                required
                                placeholder="Enter property key"
                                value={field.property_key}
                                onChange={e => handleFieldChange(index, 'property_key', e.target.value)}
                                maxLength={32}
                                style={{
                                    borderColor: isDuplicateKey ? '#d9534f' : undefined,
                                    backgroundColor: isDuplicateKey ? '#fff5f5' : undefined
                                }}
                            />
                            {isDuplicateKey && <InputError>This key is already used</InputError>}
                            <Button
                                type="button"
                                variant="secondary-gray"
                                p={1}
                                sx={{ cursor: 'pointer', '& svg': { marginRight: 0 } }}
                                onClick={() => handleDeleteField(index)}
                            >
                                <FiTrash2 size={16} />
                            </Button>
                        </React.Fragment>
                    );
                })}
            </CustomFieldsGrid>

            {customFields.length >= MAX_CUSTOM_FIELDS && (
                <Box className="fs-12 warning" mt={'6px'}>
                    Warning: adding more fields may result in survey URLs longer than 2000 characters, which may not
                    work in older browsers.
                </Box>
            )}
            <Flex justifyContent="space-between" alignItems="center" mt={3}>
                <Button type="button" variant="secondary" onClick={handleAddField}>
                    <FiPlus /> Add Custom Field
                </Button>
                {showSaveButton && (
                    <Flex alignItems="center">
                        <Button
                            type="submit"
                            variant="primary"
                            className="modal-primary"
                            onClick={handleSave}
                            disabled={isSaveButtonDisabled}
                            mr={0}
                        >
                            Save
                        </Button>
                    </Flex>
                )}
            </Flex>
        </Box>
    );
}

export default ScreenerIntegrationCustomFields;
